import { all, call, fork, put, takeEvery } from 'redux-saga/effects';
import {
  DEALER_FETCH,
  DEALERS_FETCH,
  DEALER_CREATE,
  DEALER_UPDATE,
  DEALER_DELETE,
  DEALERS_EXPORT_EXCEL,
} from '../redux/actions/types';
import { dealerFetch as dealerFetchAction, dealerFetchSuccess, dealersFetchSuccess } from '../redux/actions/DealerActions';
import { hideLoader, showErrorMessage, showSuccessMessage } from '../redux/actions/NotifyActions';
import { authorizationHeader, getErrorMessage } from '../util/apiUtil';
import { reset } from 'redux-form';
import { intl } from '../i18n/IntlGlobalProvider';
import axios from 'axios';
import FileSaver from 'file-saver';
import moment from 'moment';
import _ from 'lodash';

// API
const dealerFetchRequest = async id =>
  await axios
    .get(`${process.env.REACT_APP_API_URL}/api/dealers/${id}`, {
      headers: authorizationHeader('bearer'),
    })
    .then(response => response.data)
    .catch(error => {
      throw error;
    });

const dealersFetchRequest = async payload => {
  let url = `${process.env.REACT_APP_API_URL}/api/dealers`;
  let params = { ...payload };
  params = _.omitBy(params, _.isEmpty);

  return await axios
    .get(url, {
      headers: authorizationHeader('bearer'),
      params: params,
    })
    .then(response => response.data)
    .catch(error => {
      throw error;
    });
};

const dealersExportExcelRequest = async payload => {
  let url = `${process.env.REACT_APP_API_URL}/api/dealers/report/excel`;
  let params = { ...payload };
  params = _.omitBy(params, _.isEmpty);

  return await axios
    .get(url, {
      headers: authorizationHeader('bearer'),
      params: params,
      responseType: 'blob',
    })
    .then(response => {
      return response.data;
    })
    .catch(error => {
      throw error;
    });
};

const dealerCreateRequest = async values =>
  await axios
    .post(`${process.env.REACT_APP_API_URL}/api/dealers`, values, {
      headers: authorizationHeader('bearer'),
    })
    .then(response => response.data)
    .catch(error => {
      throw error;
    });

const dealerUpdateRequest = async values =>
  await axios
    .put(`${process.env.REACT_APP_API_URL}/api/dealers/${values._id}`, values, {
      headers: authorizationHeader('bearer'),
    })
    .then(response => response.data)
    .catch(error => {
      throw error;
    });

const dealerDeleteRequest = async id =>
  await axios
    .delete(`${process.env.REACT_APP_API_URL}/api/dealers/${id}`, {
      headers: authorizationHeader('bearer'),
    })
    .then(response => response.data)
    .catch(error => {
      throw error;
    });

// WORKER
function* dealerFetch({ payload }) {
  try {
    const data = yield call(dealerFetchRequest, payload);
    yield put(dealerFetchSuccess(data));
  } catch (error) {
    yield put(showErrorMessage(getErrorMessage(error)));
  }
}

function* dealersFetch({ payload }) {
  try {
    const data = yield call(dealersFetchRequest, payload);
    yield put(dealersFetchSuccess(data));
    // WITHOUT THIS ANY CALLER THAT SHOWS A LOADER FIRST WOULD HANG ON IT
    // FOREVER — usersFetch IN UserSagas.js ALREADY DOES THE SAME.
    yield put(hideLoader());
  } catch (error) {
    yield put(showErrorMessage(getErrorMessage(error)));
  }
}

function* dealersExportExcel({ payload }) {
  try {
    const data = yield call(dealersExportExcelRequest, payload);
    FileSaver.saveAs(data, `dealers_${moment().format('DDMMYYYY')}.xlsx`);
    yield put(hideLoader());
  } catch (error) {
    yield put(showErrorMessage(getErrorMessage(error)));
  }
}

function* dealerCreate({ payload }) {
  try {
    const data = yield call(dealerCreateRequest, payload);
    yield put(showSuccessMessage(intl.formatMessage({ id: 'success.updated' })));
    yield put(reset('DealerForm'));
  } catch (error) {
    yield put(showErrorMessage(getErrorMessage(error)));
  }
}

function* dealerUpdate({ payload }) {
  try {
    const data = yield call(dealerUpdateRequest, payload);
    if (data.success) {
      // PUT /api/dealers/:_id IS MISSING { new: true }, SO success.result IS THE
      // DOCUMENT AS IT WAS *BEFORE* THE EDIT. RE-FETCH INSTEAD OF TRUSTING IT,
      // OR THE FORM REPAINTS WITH THE OLD VALUES AFTER A SUCCESSFUL SAVE.
      yield put(dealerFetchAction(payload._id));
      yield put(showSuccessMessage(intl.formatMessage({ id: 'success.updated' })));
    }
  } catch (error) {
    yield put(showErrorMessage(getErrorMessage(error)));
  }
}

function* dealerDelete({ payload: { id } }) {
  try {
    const data = yield call(dealerDeleteRequest, id);
  } catch (error) {
    yield put(showErrorMessage(getErrorMessage(error)));
  }
}

// WATCHER
function* dealerFetchWatcher() {
  yield takeEvery(DEALER_FETCH, dealerFetch);
}

function* dealersFetchWatcher() {
  yield takeEvery(DEALERS_FETCH, dealersFetch);
}

function* dealerCreateWatcher() {
  yield takeEvery(DEALER_CREATE, dealerCreate);
}

function* dealerUpdateWatcher() {
  yield takeEvery(DEALER_UPDATE, dealerUpdate);
}

function* dealerDeleteWatcher() {
  yield takeEvery(DEALER_DELETE, dealerDelete);
}

function* dealersExportExcelWatcher() {
  yield takeEvery(DEALERS_EXPORT_EXCEL, dealersExportExcel);
}

export default function* rootSaga() {
  yield all([
    fork(dealerFetchWatcher),
    fork(dealersFetchWatcher),
    fork(dealerCreateWatcher),
    fork(dealerUpdateWatcher),
    fork(dealerDeleteWatcher),
    fork(dealersExportExcelWatcher),
  ]);
}
